
import { toast } from 'react-hot-toast';
import { Order, Driver, DeliveryProvider, SystemConfig } from '../types';
import { DEFAULT_CONFIG } from '../constants';
import { deobfuscate, obfuscate } from './parserService';

const BACKUP_VERSION = "3.2"; // Versão atual do sistema

interface SystemBackup {
  version: string;
  timestamp: number;
  data: {
    orders: Order[];
    drivers: Driver[];
    providers: DeliveryProvider[];
    config: SystemConfig;
    simpleView: boolean;
  };
}

/**
 * Helper to safely read and deobfuscate from localStorage
 */
const safeRead = (key: string, defaultValue: string) => {
    const raw = localStorage.getItem(key);
    if (!raw) return JSON.parse(defaultValue);
    try {
        const decrypted = deobfuscate(raw);
        return JSON.parse(decrypted);
    } catch (e) {
        // Fallback for non-obfuscated legacy data
        try {
            return JSON.parse(raw);
        } catch (err) {
            return JSON.parse(defaultValue);
        }
    }
};

/**
 * Gera um arquivo JSON com todos os dados salvos no localStorage
 */
export const exportSystemBackup = () => {
  try {
    const backup: SystemBackup = {
      version: BACKUP_VERSION,
      timestamp: Date.now(),
      data: {
        orders: safeRead('sal_orders', '[]'),
        drivers: safeRead('sal_drivers', '[]'),
        providers: safeRead('sal_providers', '[]'),
        config: safeRead('sal_config', JSON.stringify(DEFAULT_CONFIG)),
        simpleView: safeRead('sal_simple_view', 'false'),
      }
    };

    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    const date = new Date().toISOString().split('T')[0];
    link.href = url;
    link.download = `SAL_ROTA_BACKUP_${date}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success('Backup gerado com sucesso!');
  } catch (error) {
    console.error('Erro ao exportar backup:', error);
    toast.error('Falha ao gerar arquivo de backup.');
  }
};

/**
 * Lê um arquivo de backup e restaura o estado do sistema
 * Utiliza lógica de mesclagem para garantir compatibilidade futura
 */
export const importSystemBackup = (file: File): Promise<boolean> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const backup = JSON.parse(content) as SystemBackup;

        // Validação básica de integridade
        if (!backup.version || !backup.data) {
          throw new Error("Arquivo de backup inválido ou corrompido.");
        }

        // --- LÓGICA DE COMPATIBILIDADE FUTURA (Schema Migration) ---
        const currentConfig = { ...DEFAULT_CONFIG, ...backup.data.config };
        
        // Persistência Atômica com Obfuscação (Mantendo padrão do sistema)
        localStorage.setItem('sal_orders', obfuscate(JSON.stringify(backup.data.orders || [])));
        localStorage.setItem('sal_drivers', obfuscate(JSON.stringify(backup.data.drivers || [])));
        localStorage.setItem('sal_providers', obfuscate(JSON.stringify(backup.data.providers || [])));
        localStorage.setItem('sal_config', obfuscate(JSON.stringify(currentConfig)));
        localStorage.setItem('sal_simple_view', obfuscate(JSON.stringify(backup.data.simpleView || false)));

        toast.success(`Backup v${backup.version} restaurado! Reiniciando...`);
        
        setTimeout(() => {
          window.location.reload();
        }, 1500);

        resolve(true);
      } catch (error) {
        console.error('Erro ao importar backup:', error);
        toast.error('Arquivo incompatível ou corrompido.');
        resolve(false);
      }
    };

    reader.onerror = () => {
      toast.error('Erro na leitura do arquivo.');
      resolve(false);
    };

    reader.readAsText(file);
  });
};

/**
 * Limpa todos os dados operacionais do sistema
 */
export const performFactoryReset = () => {
  const keysToRemove = [
    'sal_orders', 
    'sal_drivers', 
    'sal_providers', 
    'sal_config', 
    'sal_simple_view',
    'sal_auth_token',
    'sal_geo_cache_v1',
    'sal_matrix_cache_v1',
    'sal_sofia_history_v1'
  ];
  
  keysToRemove.forEach(key => localStorage.removeItem(key));
  toast.success('Sistema redefinido com sucesso!');
  
  setTimeout(() => {
    window.location.reload();
  }, 1000);
};
